import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import pLimit from 'p-limit';
import backend from '../../services/backend';
import { useToast } from '../../components/Toast';
import { reclassifyResultToast } from '../../lib/reclassifyFailedDocumentsMessage';

export type CategoryAction = 'train' | 'reclassify' | 'rebuild';

/**
 * Unifies the three category actions (train / reclassify / rebuild) behind a
 * single pending state so the header buttons can disable consistently while
 * any one of them runs. `pendingLabel` carries the in-progress button text,
 * including a percentage for the page-by-page rebuild batch.
 */
export function useCategoryActions({
  categoryId,
  pageIds,
  onActionComplete,
}: {
  categoryId: string | undefined;
  pageIds: string[];
  onActionComplete: () => void;
}) {
  const toast = useToast();
  const [rebuildProgress, setRebuildProgress] = useState<string | null>(null);

  const trainMutation = useMutation({
    mutationFn: () => backend.trainCategory(categoryId!).then((res) => res.data),
    onSuccess: () => {
      toast.success('Category trained successfully');
      onActionComplete();
    },
  });

  const reclassifyMutation = useMutation({
    mutationFn: () => backend.reclassifyCategory(categoryId!).then((res) => res.data),
    onSuccess: (data) => {
      const result = reclassifyResultToast({
        success: data.success,
        failedDocumentIds: data.failedDocumentIds,
      });
      if (result.level === 'error') {
        toast.error(result.message);
      } else {
        toast.success(result.message);
      }
      onActionComplete();
    },
  });

  const rebuildMutation = useMutation({
    mutationFn: async () => {
      const limit = pLimit(10);
      const total = pageIds.length;
      const progress = { done: 0 };

      return Promise.all(
        pageIds.map((pageId) =>
          limit(async () => {
            await backend.pageRetry(pageId, 'pdf-parser');
            progress.done += 1;
            setRebuildProgress(`Resetting ${Math.round((progress.done / total) * 100)}%...`);
          }),
        ),
      );
    },
    onSuccess: () => {
      toast.success('PDF parser reset for all pages');
      onActionComplete();
    },
    onSettled: () => setRebuildProgress(null),
  });

  const pendingAction: CategoryAction | null = trainMutation.isPending
    ? 'train'
    : reclassifyMutation.isPending
      ? 'reclassify'
      : rebuildMutation.isPending
        ? 'rebuild'
        : null;

  const pendingLabel =
    pendingAction === 'train'
      ? 'Training...'
      : pendingAction === 'reclassify'
        ? 'Reclassifying...'
        : pendingAction === 'rebuild'
          ? (rebuildProgress ?? 'Resetting...')
          : null;

  return {
    train: () => trainMutation.mutate(),
    reclassify: () => reclassifyMutation.mutate(),
    rebuild: () => rebuildMutation.mutate(),
    pendingAction,
    pendingLabel,
  };
}

export type CategoryActions = ReturnType<typeof useCategoryActions>;
